



import React, { useEffect, useState } from "react";
import AuthService from "../../services/auth.service";
import * as XLSX from 'xlsx';

const allColumns = [
  "LOGIN_ID", "NISM_NAME", "TERMINAL_TYPE", "LOCA_ID_OFFL_ONL",
  "ESL_BACKOFFICE", "KSL_BACKOFFICE", "BR_AP_TERMINAL", "SUBBROKER_NAME", "CITY",
  "SEGMENT", "First_NUMBER", "Secound_NUMBER", "Third_NUMBER", "DID_Number",
  "EMAIL", "ADDRESS", "FO_NISM", "COMMODITY_NISM", "CDS_NISM",
  "DATE_OF_ACTIVATION", "DATE_OF_DEACTIVATION", "NISM_REMARK", "BOID", "ACTIVATION_STATUS", "PANNO", "REMARK", "DOB"
];

const visibleColumns = [
  "LOGIN_ID", 
  "NISM_NAME", 
  "TERMINAL_TYPE", 
  "PANNO",
  "FO_NISM",
  "COMMODITY_NISM",
  "CDS_NISM",
  "ACTIVATION_STATUS"
];

const dateColumns = [
  "DOB", "DATE_OF_ACTIVATION", "DATE_OF_DEACTIVATION", 
  "FO_NISM", "COMMODITY_NISM", "CDS_NISM"
];

function calculateDaysRemaining(dateString) {
  if (!dateString) return { text: 'N/A', status: 'neutral', diffDays: null };

  try {
    const targetDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    targetDate.setHours(0, 0, 0, 0);

    const diffTime = targetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > 30) {
      return { text: `${diffDays} days remaining`, status: 'future', diffDays };
    } else if (diffDays > 0) {
      return { text: `${diffDays} days remaining`, status: 'warning', diffDays };
    } else if (diffDays < 0) {
      return { text: `${Math.abs(diffDays)} days ago`, status: 'expired', diffDays };
    } else {
      return { text: 'Today', status: 'today', diffDays };
    }
  } catch (e) {
    console.error("Error parsing date:", dateString);
    return { text: 'Invalid date', status: 'error', diffDays: null };
  }
}

export default function CdclManager() {
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [viewRecord, setViewRecord] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    TERMINAL_TYPE: "",
    LOCA_ID_OFFL_ONL: "",
    BR_AP_TERMINAL: "",
    CITY: "",
    SEGMENT: "",
    DAYS_REMAINING: "",
    ACTIVATION_STATUS: ""
  });

  const [showAddForm, setShowAddForm] = useState(false);
  const [newRecord, setNewRecord] = useState({ ACTIVATION_STATUS: "A" });
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  const uniqueValues = {
    TERMINAL_TYPE: [...new Set(records.map(r => r.TERMINAL_TYPE))].filter(Boolean),
    LOCA_ID_OFFL_ONL: [...new Set(records.map(r => r.LOCA_ID_OFFL_ONL))].filter(Boolean),
    BR_AP_TERMINAL: [...new Set(records.map(r => r.BR_AP_TERMINAL))].filter(Boolean),
    CITY: [...new Set(records.map(r => r.CITY))].filter(Boolean),
    SEGMENT: [...new Set(records.map(r => r.SEGMENT))].filter(Boolean),
    ACTIVATION_STATUS: ["A", "I"]
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  useEffect(() => {
    filterRecords();
  }, [searchTerm, filters, records]);

  async function fetchRecords() {
    try {
      const data = await AuthService.getAllCdcl();
      setRecords(data);
      setFilteredRecords(data);
    } catch (error) {
      console.error(error);
      displayAlert("Error fetching records");
    }
  }

  function filterRecords() {
    let result = records;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(record => 
        ["LOGIN_ID", "NISM_NAME", "ESL_BACKOFFICE", "KSL_BACKOFFICE", "SUBBROKER_NAME", 
         "First_NUMBER", "Secound_NUMBER", "Third_NUMBER", "DID_Number", "EMAIL"]
        .some(field => 
          record[field] && record[field].toString().toLowerCase().includes(term)
        )
      );
    }
    
    if (showFilters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value && key !== "DAYS_REMAINING") {
          result = result.filter(record => 
            record[key] && record[key].toString() === value
          );
        }
      });

      if (filters.DAYS_REMAINING) {
        const daysThreshold = parseInt(filters.DAYS_REMAINING);
        result = result.filter(record => {
          const dateFields = ["FO_NISM", "COMMODITY_NISM", "CDS_NISM"];
          return dateFields.some(field => {
            if (!record[field]) return false;
            const { diffDays } = calculateDaysRemaining(record[field]);
            return diffDays !== null && diffDays <= daysThreshold;
          });
        });
      }
    }
    
    setFilteredRecords(result);
  }

  function handleFilterChange(e, field) {
    setFilters({
      ...filters,
      [field]: e.target.value
    });
  }

  function handleNewChange(e, field) {
    setNewRecord({ ...newRecord, [field]: e.target.value });
  }

  function displayAlert(message) {
    setAlertMessage(message);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 3000);
  }

  async function handleAddRecord() {
    const requiredFields = [

    ];
  
    const numberFields = ["First_NUMBER", "Secound_NUMBER", "Third_NUMBER", "DID_Number", "BOID"];
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  
    for (const field of requiredFields) {
      if (!newRecord[field] || newRecord[field].trim() === "") {
        displayAlert(`Field "${field}" is required.`);
        return;
      }
    }
  
    for (const field of numberFields) {
      if (newRecord[field] && isNaN(newRecord[field])) {
        displayAlert(`Field "${field}" must be a number.`);
        return;
      }
    }
  
    if (newRecord.EMAIL && !emailRegex.test(newRecord.EMAIL)) {
      displayAlert("Please enter a valid email address.");
      return;
    }
  
    if (newRecord.PANNO && !panRegex.test(newRecord.PANNO)) {
      displayAlert("Invalid PAN format. Expected format: ABCDE1234F");
      return;
    }
  
    try {
      await AuthService.addCdcl(newRecord);
      setNewRecord({ ACTIVATION_STATUS: "A" });
      setShowAddForm(false);
      fetchRecords();
      displayAlert("Record added successfully!");
    } catch (error) {
      console.error(error);
      displayAlert("Error adding record.");
    }
  }

  function handleView(record) {
    setViewRecord(record);
    setEditRecord(null);
  }

  function handleEdit(record) {
    setEditRecord(record);
  }

  function handleChange(e, field) {
    setEditRecord({ ...editRecord, [field]: e.target.value });
  }

  async function toggleStatus(record) {
    const newStatus = record.ACTIVATION_STATUS === "A" ? "I" : "A";
    const action = newStatus === "A" ? "activate" : "deactivate";
    
    setAlertMessage(`Are you sure you want to ${action} this record?`);
    setShowAlert(true);
  }

  async function confirmStatusChange(confirmed) {
    if (!confirmed) {
      setShowAlert(false);
      return;
    }

    const record = viewRecord || editRecord;
    const newStatus = record.ACTIVATION_STATUS === "A" ? "I" : "A";
    
    try {
      const response = await AuthService.updateCdclstatus(record.id, newStatus);
      
      if (response.success) {
        await fetchRecords();
        
        if (viewRecord && viewRecord.id === record.id) {
          setViewRecord({ ...viewRecord, ACTIVATION_STATUS: newStatus });
        }
        if (editRecord && editRecord.id === record.id) {
          setEditRecord({ ...editRecord, ACTIVATION_STATUS: newStatus });
        }
        
        displayAlert(response.message || "Status updated successfully");
      } else {
        throw new Error(response.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      displayAlert(error.message || "Failed to update status");
    }
    setShowAlert(false);
  }

  async function handleSave() {
    try {
      await AuthService.updateCdcl(editRecord);
      setEditRecord(null);
      setViewRecord(null);
      fetchRecords();
      displayAlert("Record updated successfully!");
    } catch (error) {
      console.error(error);
      displayAlert("Error updating record.");
    }
  }

  function handleCloseView() {
    setViewRecord(null);
    setEditRecord(null);
  }

  function getStatusColor(status) {
    switch(status) {
      case 'future': return '#4CAF50';
      case 'warning': return '#FF9800';
      case 'expired': return '#F44336';
      case 'today': return '#2196F3';
      case 'error': return '#9E9E9E';
      default: return '#666';
    }
  }



const downloadExcel = async () => {
  setIsExporting(true);
  
  try {
    // Validate data first
    if (!records || records.length === 0) {
      throw new Error("No records available to export");
    }

    // Prepare the data with proper formatting
    const excelData = records.map(record => {
      // Helper function to format dates safely
      const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
          const date = new Date(dateString);
          return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
        } catch {
          return '';
        }
      };

      // Helper function to clean text fields
      const cleanText = (text) => {
        if (text === null || text === undefined) return '';
        return String(text)
          .replace(/\t/g, ' ')  // Replace tabs
          .replace(/\r?\n/g, ' '); // Replace newlines
      };

      return {
        "LOGIN_ID": cleanText(record.LOGIN_ID),
        "NISM_NAME": cleanText(record.NISM_NAME),
        "TERMINAL_TYPE": cleanText(record.TERMINAL_TYPE),
        "LOCA_ID_OFFL_ONL": cleanText(record.LOCA_ID_OFFL_ONL),
        "ESL_BACKOFFICE": cleanText(record.ESL_BACKOFFICE),
        "KSL_BACKOFFICE": cleanText(record.KSL_BACKOFFICE),
        "BR_AP_TERMINAL": cleanText(record.BR_AP_TERMINAL),
        "SUBBROKER_NAME": cleanText(record.SUBBROKER_NAME),
        "CITY": cleanText(record.CITY),
        "SEGMENT": cleanText(record.SEGMENT),
        "First_NUMBER": cleanText(record.First_NUMBER),
        "Secound_NUMBER": cleanText(record.Secound_NUMBER),
        "Third_NUMBER": cleanText(record.Third_NUMBER),
        "DID_Number": cleanText(record.DID_Number),
        "EMAIL": cleanText(record.EMAIL),
        "ADDRESS": cleanText(record.ADDRESS),
        "FO_NISM": formatDate(record.FO_NISM),
        "COMMODITY_NISM": formatDate(record.COMMODITY_NISM),
        "CDS_NISM": formatDate(record.CDS_NISM),
        "DATE_OF_ACTIVATION": formatDate(record.DATE_OF_ACTIVATION),
        "DATE_OF_DEACTIVATION": formatDate(record.DATE_OF_DEACTIVATION),
        "NISM_REMARK": cleanText(record.NISM_REMARK),
        "BOID": cleanText(record.BOID),
        "ACTIVATION_STATUS": cleanText(record.ACTIVATION_STATUS),
        "PANNO": cleanText(record.PANNO),
        "REMARK": cleanText(record.REMARK),
        "DOB": formatDate(record.DOB)
      };
    });

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);
    
    // Set column widths (adjust as needed)
    const wscols = [
      {wch: 15}, // LOGIN_ID
      {wch: 25}, // NISM_NAME
      {wch: 15}, // TERMINAL_TYPE
      {wch: 15}, // LOCA_ID_OFFL_ONL
      {wch: 15}, // ESL_BACKOFFICE
      {wch: 15}, // KSL_BACKOFFICE
      {wch: 15}, // BR_AP_TERMINAL
      {wch: 30}, // SUBBROKER_NAME
      {wch: 15}, // CITY
      {wch: 20}, // SEGMENT
      {wch: 15}, // First_NUMBER
      {wch: 15}, // Secound_NUMBER
      {wch: 15}, // Third_NUMBER
      {wch: 15}, // DID_Number
      {wch: 25}, // EMAIL
      {wch: 40}, // ADDRESS
      {wch: 12}, // FO_NISM
      {wch: 12}, // COMMODITY_NISM
      {wch: 12}, // CDS_NISM
      {wch: 12}, // DATE_OF_ACTIVATION
      {wch: 12}, // DATE_OF_DEACTIVATION
      {wch: 30}, // NISM_REMARK
      {wch: 20}, // BOID
      {wch: 5},  // ACTIVATION_STATUS
      {wch: 15}, // PANNO
      {wch: 30}, // REMARK
      {wch: 12}  // DOB
    ];
    ws['!cols'] = wscols;

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "CDCL Records");
    
    // Generate Excel file and download
    XLSX.writeFile(wb, `CDCL_Records_${new Date().toISOString().slice(0, 10)}.xlsx`, {
      bookType: 'xlsx',
      type: 'array'
    });
    
    displayAlert(`Successfully exported ${records.length} records`);
  } catch (error) {
    console.error("Excel export error:", error);
    displayAlert(`Export failed: ${error.message}`);
  } finally {
    setIsExporting(false);
  }
};


  const ActivationToggle = ({ status, onChange }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ fontWeight: 'bold' }}>A</span>
      <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '24px' }}>
        <input 
          type="checkbox" 
          checked={status === "I"}
          onChange={onChange}
          style={{ opacity: 0, width: 0, height: 0 }}
        />
        <span style={{
          position: 'absolute',
          cursor: 'pointer',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: status === "A" ? '#4CAF50' : '#f44336',
          transition: '.4s',
          borderRadius: '24px'
        }}>
          <span style={{
            position: 'absolute',
            height: '16px',
            width: '16px',
            left: status === "A" ? '4px' : '30px',
            bottom: '4px',
            backgroundColor: 'white',
            transition: '.4s',
            borderRadius: '50%'
          }} />
        </span>
      </label>
      <span style={{ fontWeight: 'bold' }}>I</span>
    </div>
  );

  return (
    <div style={{ padding: 20, position: 'relative' }}>
      {/* Alert Modal */}
      {showAlert && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(5px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            maxWidth: '500px',
            textAlign: 'center'
          }}>
            <h3 style={{ marginTop: 0 }}>{alertMessage}</h3>
            {alertMessage.startsWith("Are you sure") && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '20px' }}>
                <button 
                  onClick={() => confirmStatusChange(true)}
                  style={{
                    padding: '8px 20px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Yes
                </button>
                <button 
                  onClick={() => confirmStatusChange(false)}
                  style={{
                    padding: '8px 20px',
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  No
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search and Filter Section */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 10, gap: "10px", flexWrap: "wrap" }}>
          <input
            type="text"
            placeholder="Search by ID, Name, Phone, Email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ 
              padding: "8px 12px", 
              width: "400px", 
              borderRadius: "4px", 
              border: "1px solid #ccc" 
            }}
          />

          <button 
            onClick={() => setShowFilters(!showFilters)} 
            style={{ 
              padding: "8px 12px",
              backgroundColor: showFilters ? "#ddd" : "#eee",
              border: "1px solid #ccc",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </button>
          
          {/* Export Button */}
          <button 
  onClick={downloadExcel}
  disabled={isExporting}
  style={{ 
    padding: "8px 12px",
    backgroundColor: isExporting ? "#757575" : "#2196F3",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "5px",
    minWidth: "120px",
    marginLeft: "auto"
  }}
>
  {isExporting ? (
    <>
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
        <polyline points="7 10 12 15 17 10"></polyline>
        <line x1="12" y1="15" x2="12" y2="3"></line>
      </svg>
      Exporting...
    </>
  ) : (
    <>
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
        <polyline points="7 10 12 15 17 10"></polyline>
        <line x1="12" y1="15" x2="12" y2="3"></line>
      </svg>
      Export to Excel
    </>
  )}
</button>

          <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
            <label style={{ 
              padding: "8px 12px", 
              width: "100px", 
            }}>
              Days Remaining ≤
            </label>
            <input
              type="number"
              value={filters.DAYS_REMAINING}
              onChange={(e) => handleFilterChange(e, "DAYS_REMAINING")}
              style={{ 
                padding: "8px 12px", 
                width: "400px", 
                borderRadius: "4px", 
                border: "1px solid #ccc" 
              }}
              placeholder="Enter days"
              min="0"
            />
          </div>
        </div>
        
        {showFilters && (
          <div style={{ 
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
            gap: "15px", 
            padding: "15px",
            backgroundColor: "#f5f5f5",
            borderRadius: "4px",
            marginBottom: "15px"
          }}>
            {/* Filter dropdowns */}
            <div>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Terminal Type</label>
              <select
                value={filters.TERMINAL_TYPE}
                onChange={(e) => handleFilterChange(e, "TERMINAL_TYPE")}
                style={{ width: "100%", padding: "8px" }}
              >
                <option value="">All</option>
                {uniqueValues.TERMINAL_TYPE.map(value => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Location ID</label>
              <select
                value={filters.LOCA_ID_OFFL_ONL}
                onChange={(e) => handleFilterChange(e, "LOCA_ID_OFFL_ONL")}
                style={{ width: "100%", padding: "8px" }}
              >
                <option value="">All</option>
                {uniqueValues.LOCA_ID_OFFL_ONL.map(value => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>BR/AP Terminal</label>
              <select
                value={filters.BR_AP_TERMINAL}
                onChange={(e) => handleFilterChange(e, "BR_AP_TERMINAL")}
                style={{ width: "100%", padding: "8px" }}
              >
                <option value="">All</option>
                {uniqueValues.BR_AP_TERMINAL.map(value => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>City</label>
              <select
                value={filters.CITY}
                onChange={(e) => handleFilterChange(e, "CITY")}
                style={{ width: "100%", padding: "8px" }}
              >
                <option value="">All</option>
                {uniqueValues.CITY.map(value => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Segment</label>
              <select
                value={filters.SEGMENT}
                onChange={(e) => handleFilterChange(e, "SEGMENT")}
                style={{ width: "100%", padding: "8px" }}
              >
                <option value="">All</option>
                {uniqueValues.SEGMENT.map(value => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Status</label>
              <select
                value={filters.ACTIVATION_STATUS}
                onChange={(e) => handleFilterChange(e, "ACTIVATION_STATUS")}
                style={{ width: "100%", padding: "8px" }}
              >
                <option value="">All</option>
                <option value="A">Active</option>
                <option value="I">Inactive</option>
              </select>
            </div>

            <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end" }}>
              <button 
                onClick={() => setFilters({
                  TERMINAL_TYPE: "",
                  LOCA_ID_OFFL_ONL: "",
                  BR_AP_TERMINAL: "",
                  CITY: "",
                  SEGMENT: "",
                  ACTIVATION_STATUS: "",
                  DAYS_REMAINING: ""
                })}
                style={{ 
                  padding: "8px 16px",
                  backgroundColor: "#f0f0f0",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  cursor: "pointer"
                }}
              >
                Clear All Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Button */}
      <button 
        onClick={() => setShowAddForm(!showAddForm)} 
        style={{ 
          marginBottom: 20,
          padding: "8px 16px",
          backgroundColor: "#4CAF50",
          color: "white",
          border: "none",
          borderRadius: "4px",
          cursor: "pointer"
        }}
      >
        {showAddForm ? "Cancel" : "Add New Record"}
      </button>

      {/* Add Form */}
      {showAddForm && (
        <div
          style={{
            marginBottom: 20,
            padding: 15,
            border: "1px solid #ccc",
            borderRadius: 5,
            backgroundColor: "#fafafa",
            maxHeight: "60vh",
            overflowY: "auto",
          }}
        >
          <h3>Add New Record</h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {allColumns.map((col) => (
              <div key={col} style={{ minWidth: 200, display: "flex", flexDirection: "column" }}>
                <label style={{ fontWeight: "bold" }}>{col}</label>
                {col === "ACTIVATION_STATUS" ? (
                  <ActivationToggle
                    status={newRecord[col] || "A"}
                    onChange={() => handleNewChange(
                      { target: { value: newRecord[col] === "A" ? "I" : "A" } }, 
                      col
                    )}
                  />
                ) : dateColumns.includes(col) ? (
                  <input
                    type="date"
                    value={newRecord[col] || ""}
                    onChange={(e) => handleNewChange(e, col)}
                    style={{ padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
                  />
                ) : ["First_NUMBER", "Secound_NUMBER", "Third_NUMBER", "DID_Number", "BOID"].includes(col) ? (
                  <input
                    type="number"
                    value={newRecord[col] || ""}
                    onChange={(e) => handleNewChange(e, col)}
                    style={{ padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
                  />
                ) : (
                  <input
                    type="text"
                    value={newRecord[col] || ""}
                    onChange={(e) => handleNewChange(e, col)}
                    style={{ padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
                  />
                )}
              </div>
            ))}
          </div>
          <button 
            onClick={handleAddRecord} 
            style={{ 
              marginTop: 15,
              padding: "8px 16px",
              backgroundColor: "#4CAF50",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            Submit
          </button>
        </div>
      )}

      {/* Main Table with Fixed Header */}
      <div style={{ 
        overflowX: "auto",
        height: "calc(100vh - 300px)",
        overflowY: "auto",
        position: "relative"
      }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ 
            position: "sticky", 
            top: 0, 
            zIndex: 10,
            backgroundColor: "#f2f2f2"
          }}>
            <tr>
              {visibleColumns.map((col) => (
                <th key={col} style={{ 
                  padding: "12px", 
                  textAlign: "left", 
                  borderBottom: "1px solid #ddd",
                  position: "sticky",
                  top: 0,
                  backgroundColor: "#f2f2f2"
                }}>
                  {col}
                </th>
              ))}
              <th style={{ 
                padding: "12px", 
                textAlign: "left", 
                borderBottom: "1px solid #ddd",
                position: "sticky",
                top: 0,
                backgroundColor: "#f2f2f2"
              }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords.map((record) => (
              <tr key={record.sno} style={{ borderBottom: "1px solid #ddd" }}>
                {visibleColumns.map((col) => (
                  <td key={col} style={{ padding: "12px" }}>
                    {["FO_NISM", "COMMODITY_NISM", "CDS_NISM"].includes(col) ? (
                      <>
                        <div>{record[col] ? new Date(record[col]).toISOString().split('T')[0] : 'N/A'}</div>
                        <div style={{
                          fontSize: '1.1em',
                          fontWeight: 'bold',
                          color: getStatusColor(calculateDaysRemaining(record[col]).status)
                        }}>
                          {calculateDaysRemaining(record[col]).text}
                        </div>
                      </>
                    ) : col === "ACTIVATION_STATUS" ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <ActivationToggle
                          status={record[col] || "A"}
                          onChange={() => toggleStatus(record)}
                        />
                        <span style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          backgroundColor: record[col] === "A" ? "#4CAF50" : "#f44336",
                          color: "white",
                          fontWeight: "bold"
                        }}>
                          {record[col] === "A" ? "Active" : "Inactive"}
                        </span>
                      </div>
                    ) : (
                      record[col] || 'N/A'
                    )}
                  </td>
                ))}
                <td style={{ padding: "12px" }}>
                  <button 
                    onClick={() => handleView(record)}
                    style={{
                      padding: "5px 10px",
                      backgroundColor: "#2196F3",
                      color: "white",
                      border: "none",
                      borderRadius: "3px",
                      cursor: "pointer",
                      marginRight: "5px"
                    }}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* View/Edit Panel */}
      {viewRecord && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            width: "50vw",
            height: "100vh",
            backgroundColor: "#f0f0f0",
            boxShadow: "-3px 0 5px rgba(0,0,0,0.2)",
            padding: 20,
            overflowY: "auto",
            zIndex: 1000,
          }}
        >
          <h3>View Record - SNO: {viewRecord.sno}</h3>
          <button 
            onClick={handleCloseView}
            style={{
              padding: "5px 10px",
              backgroundColor: "#f44336",
              color: "white",
              border: "none",
              borderRadius: "3px",
              cursor: "pointer",
              marginBottom: "15px"
            }}
          >
            Close
          </button>

          <div style={{ 
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: "15px"
          }}>
            {allColumns.map((col) => (
              <div key={col} style={{ marginBottom: "10px" }}>
                <label style={{ fontWeight: "bold", display: "block", marginBottom: "5px" }}>{col}</label>
                {editRecord && editRecord.sno === viewRecord.sno ? (
                  col === "ACTIVATION_STATUS" ? (
                    <ActivationToggle
                      status={editRecord[col] || "A"}
                      onChange={() => handleChange(
                        { target: { value: editRecord[col] === "A" ? "I" : "A" } }, 
                        col
                      )}
                    />
                  ) : dateColumns.includes(col) ? (
                    <input
                      type="date"
                      value={editRecord[col] ? new Date(editRecord[col]).toISOString().split('T')[0] : ""}
                      onChange={(e) => handleChange(e, col)}
                      style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
                    />
                  ) : ["First_NUMBER", "Secound_NUMBER", "Third_NUMBER", "DID_Number", "BOID"].includes(col) ? (
                    <input
                      type="number"
                      value={editRecord[col] || ""}
                      onChange={(e) => handleChange(e, col)}
                      style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
                    />
                  ) : (
                    <input
                      type="text"
                      value={editRecord[col] || ""}
                      onChange={(e) => handleChange(e, col)}
                      style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
                    />
                  )
                ) : dateColumns.includes(col) ? (
                  <>
                    <div>{viewRecord[col] ? new Date(viewRecord[col]).toISOString().split('T')[0] : 'N/A'}</div>
                    {["FO_NISM", "COMMODITY_NISM", "CDS_NISM"].includes(col) && (
                      <div style={{
                        fontSize: '0.8em',
                        color: getStatusColor(calculateDaysRemaining(viewRecord[col]).status)
                      }}>
                        {calculateDaysRemaining(viewRecord[col]).text}
                      </div>
                    )}
                  </>
                ) : col === "ACTIVATION_STATUS" ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <ActivationToggle
                      status={viewRecord[col] || "A"}
                      onChange={() => toggleStatus(viewRecord)}
                    />
                    <span style={{
                      padding: "4px 8px",
                      borderRadius: "4px",
                      backgroundColor: viewRecord[col] === "A" ? "#4CAF50" : "#f44336",
                      color: "white",
                      fontWeight: "bold"
                    }}>
                      {viewRecord[col] === "A" ? "Active" : "Inactive"}
                    </span>
                  </div>
                ) : (
                  <div>{viewRecord[col] || 'N/A'}</div>
                )}
              </div>
            ))}
          </div>

          {editRecord && editRecord.sno === viewRecord.sno ? (
            <div style={{ marginTop: "20px" }}>
              <button 
                onClick={handleSave} 
                style={{ 
                  padding: "8px 16px",
                  backgroundColor: "#4CAF50",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  marginRight: "10px"
                }}
              >
                Save
              </button>
              <button 
                onClick={() => setEditRecord(null)} 
                style={{ 
                  padding: "8px 16px",
                  backgroundColor: "#f44336",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button 
              onClick={() => handleEdit(viewRecord)} 
              style={{ 
                marginTop: "20px",
                padding: "8px 16px",
                backgroundColor: "#2196F3",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer"
              }}
            >
              Edit
            </button>
          )}
        </div>
      )}
    </div>
  );
}





























// import React, { useEffect, useState } from "react";
// // import AuthService from "../../services/auth.service";
// import AuthService from "../../services/auth.service";

// const allColumns = [
//   "LOGIN_ID","NISM_NAME","TERMINAL_TYPE","LOCA_ID_OFFL_ONL",
//   "ESL_BACKOFFICE","KSL_BACKOFFICE","BR_AP_TERMINAL","SUBBROKER_NAME","CITY",
//   "SEGMENT","First_NUMBER","secound_NUMBER","Third_NUMBER","DID_Number",
//   "EMAIL","ADDRESS","FO_NISM","COMMODITY_NISM","CDS_NISM",
//   "DATE_OF_ACTIVATION","PANNO","REMARK","DOB"
// ];

// const visibleColumns = [
//   "LOGIN_ID", 
//   "NISM_NAME", 
//   "TERMINAL_TYPE", 
//   "PANNO",
//   "FO_NISM",
//   "COMMODITY_NISM",
//   "CDS_NISM"
// ];

// // Utility function to calculate days remaining
// function calculateDaysRemaining(dateString) {
//   if (!dateString) return { text: 'N/A', status: 'neutral', diffDays: null };

//   try {
//     // Parse the ISO date format directly
//     const targetDate = new Date(dateString);
//     const today = new Date();

//     // Reset time components to avoid timezone issues
//     today.setHours(0, 0, 0, 0);
//     targetDate.setHours(0, 0, 0, 0);

//     // Calculate difference in milliseconds
//     const diffTime = targetDate - today;

//     // Convert to days
//     const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

//     if (diffDays > 30) {
//       return { text: `${diffDays} days remaining`, status: 'future', diffDays };
//     } else if (diffDays > 0) {
//       return { text: `${diffDays} days remaining`, status: 'warning', diffDays };
//     } else if (diffDays < 0) {
//       return { text: `${Math.abs(diffDays)} days ago`, status: 'expired', diffDays };
//     } else {
//       return { text: 'Today', status: 'today', diffDays };
//     }
//   } catch (e) {
//     console.error("Error parsing date:", dateString);
//     return { text: 'Invalid date', status: 'error', diffDays: null };
//   }
// }

// export default function CtclManager() {
//   const [records, setRecords] = useState([]);
//   const [filteredRecords, setFilteredRecords] = useState([]);
//   const [viewRecord, setViewRecord] = useState(null);
//   const [editRecord, setEditRecord] = useState(null);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [showFilters, setShowFilters] = useState(false);
//   const [filters, setFilters] = useState({
//     TERMINAL_TYPE: "",
//     LOCA_ID_OFFL_ONL: "",
//     BR_AP_TERMINAL: "",
//     CITY: "",
//     SEGMENT: "",
//     DAYS_REMAINING: ""
//   });

//   // For showing/hiding add form
//   const [showAddForm, setShowAddForm] = useState(false);
//   const [newRecord, setNewRecord] = useState({});

//   // Extract unique values for dropdowns
//   const uniqueValues = {
//     TERMINAL_TYPE: [...new Set(records.map(r => r.TERMINAL_TYPE))].filter(Boolean),
//     LOCA_ID_OFFL_ONL: [...new Set(records.map(r => r.LOCA_ID_OFFL_ONL))].filter(Boolean),
//     BR_AP_TERMINAL: [...new Set(records.map(r => r.BR_AP_TERMINAL))].filter(Boolean),
//     CITY: [...new Set(records.map(r => r.CITY))].filter(Boolean),
//     SEGMENT: [...new Set(records.map(r => r.SEGMENT))].filter(Boolean)
//   };

//   useEffect(() => {
//     fetchRecords();
//   }, []);

//   useEffect(() => {
//     filterRecords();
//   }, [searchTerm, filters, records]);

//   async function fetchRecords() {
//     try {
//       const data = await AuthService.getAllCdcl();
//       setRecords(data);
//       setFilteredRecords(data);
//     } catch (error) {
//       console.error(error);
//     }
//   }

//   function filterRecords() {
//     let result = records;
    
//     // Apply search term filter
//     if (searchTerm) {
//       const term = searchTerm.toLowerCase();
//       result = result.filter(record => 
//         ["LOGIN_ID", "NISM_NAME", "ESL_BACKOFFICE","KSL_BACKOFFICE", "SUBBROKER_NAME", 
//          "First_NUMBER", "secound_NUMBER", "Third_NUMBER", "DID_Number", "EMAIL"]
//         .some(field => 
//           record[field] && record[field].toString().toLowerCase().includes(term)
//         )
//       );
//     }
    
//     // Apply additional filters if they exist
//     if (showFilters) {
//       Object.entries(filters).forEach(([key, value]) => {
//         if (value && key !== "DAYS_REMAINING") {
//           result = result.filter(record => 
//             record[key] && record[key].toString() === value
//           );
//         }
//       });

//       // Apply days remaining filter if it exists
//       if (filters.DAYS_REMAINING) {
//         const daysThreshold = parseInt(filters.DAYS_REMAINING);
//         result = result.filter(record => {
//           // Check if any of the date fields have days remaining <= threshold
//           const dateFields = ["FO_NISM", "COMMODITY_NISM", "CDS_NISM"];
//           return dateFields.some(field => {
//             if (!record[field]) return false;
//             const { diffDays } = calculateDaysRemaining(record[field]);
//             return diffDays !== null && diffDays <= daysThreshold;
//           });
//         });
//       }
//     }
    
//     setFilteredRecords(result);
//   }

//   function handleFilterChange(e, field) {
//     setFilters({
//       ...filters,
//       [field]: e.target.value
//     });
//   }

//   // Handle input change for add form
//   function handleNewChange(e, field) {
//     setNewRecord({ ...newRecord, [field]: e.target.value });
//   }

//   async function handleAddRecord() {
//     // Required fields
//     const requiredFields = [
//       "LOGIN_ID", "NISM_NAME", "TERMINAL_TYPE", "LOCA_ID_OFFL_ONL",
//       "ESL_BACKOFFICE","KSL_BACKOFFICE", "BR_AP_TERMINAL", "SUBBROKER_NAME",
//       "CITY", "SEGMENT"
//     ];
  
//     // Number fields
//     const numberFields = ["First_NUMBER", "secound_NUMBER", "Third_NUMBER", "DID_Number"];
  
//     // Email pattern
//     const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
//     // PAN pattern (optional)
//     const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  
//     // 1. Check required fields
//     for (const field of requiredFields) {
//       if (!newRecord[field] || newRecord[field].trim() === "") {
//         alert(`Field "${field}" is required.`);
//         return;
//       }
//     }
  
//     // 2. Validate number fields
//     for (const field of numberFields) {
//       if (newRecord[field] && isNaN(newRecord[field])) {
//         alert(`Field "${field}" must be a number.`);
//         return;
//       }
//     }
  
//     // 3. Validate email
//     if (newRecord.EMAIL && !emailRegex.test(newRecord.EMAIL)) {
//       alert("Please enter a valid email address.");
//       return;
//     }
  
//     // 4. Optional PAN validation
//     if (newRecord.PANNO && !panRegex.test(newRecord.PANNO)) {
//       alert("Invalid PAN format. Expected format: ABCDE1234F");
//       return;
//     }
  
//     try {
//       await AuthService.addCdcl(newRecord);
//       setNewRecord({});
//       setShowAddForm(false);
//       fetchRecords();
//     } catch (error) {
//       console.error(error);
//       alert("Error adding record.");
//     }
//   }

//   // View record in panel
//   function handleView(record) {
//     setViewRecord(record);
//     setEditRecord(null);
//   }

//   // Enable editing in panel
//   function handleEdit(record) {
//     setEditRecord(record);
//   }

//   // Handle input changes in edit panel
//   function handleChange(e, field) {
//     setEditRecord({ ...editRecord, [field]: e.target.value });
//   }

//   // Save edited record
//   async function handleSave() {
//     try {
//       await AuthService.updateCdcl(editRecord);
//       setEditRecord(null);
//       setViewRecord(null);
//       fetchRecords();
//     } catch (error) {
//       console.error(error);
//     }
//   }

//   // Close view panel
//   function handleCloseView() {
//     setViewRecord(null);
//     setEditRecord(null);
//   }

//   // Get color based on status
//   function getStatusColor(status) {
//     switch(status) {
//       case 'future': return '#4CAF50';
//       case 'warning': return '#FF9800';
//       case 'expired': return '#F44336';
//       case 'today': return '#2196F3';
//       case 'error': return '#9E9E9E';
//       default: return '#666';
//     }
//   }

//   return (
//     <div style={{ padding: 20 }}>
//       {/* Search and Filter Section */}
//       <div style={{ marginBottom: 20 }}>
//         <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
//           <input
//             type="text"
//             placeholder="Search by ID, Name, Boss ID, Phone, Email..."
//             value={searchTerm}
//             onChange={(e) => setSearchTerm(e.target.value)}
//             style={{ 
//               padding: "8px 12px", 
//               width: "400px", 
//               borderRadius: "4px", 
//               border: "1px solid #ccc" 
//             }}
//           />
//           <button 
//             onClick={() => setShowFilters(!showFilters)} 
//             style={{ 
//               marginLeft: "10px", 
//               padding: "8px 12px",
//               backgroundColor: showFilters ? "#62cccf" : "#62cccf",
//               border: "1px solid #ccc",
//               borderRadius: "4px",
//               cursor: "pointer"
//             }}
//           >
//             {showFilters ? "Hide Filters" : "Show Filters"}
//           </button>
//         </div>
        
//         {showFilters && (
//           <div style={{ 
//             display: "grid",
//             gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
//             gap: "15px", 
//             padding: "15px",
//             backgroundColor: "#f5f5f5",
//             borderRadius: "4px",
//             marginBottom: "15px"
//           }}>
//             {/* TERMINAL_TYPE Dropdown */}
//             <div>
//               <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Terminal Type</label>
//               <select
//                 value={filters.TERMINAL_TYPE}
//                 onChange={(e) => handleFilterChange(e, "TERMINAL_TYPE")}
//                 style={{ width: "100%", padding: "8px" }}
//               >
//                 <option value="">All</option>
//                 {uniqueValues.TERMINAL_TYPE.map(value => (
//                   <option key={value} value={value}>{value}</option>
//                 ))}
//               </select>
//             </div>

//             {/* LOCA_ID_OFFL_ONL Dropdown */}
//             <div>
//               <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Location ID</label>
//               <select
//                 value={filters.LOCA_ID_OFFL_ONL}
//                 onChange={(e) => handleFilterChange(e, "LOCA_ID_OFFL_ONL")}
//                 style={{ width: "100%", padding: "8px" }}
//               >
//                 <option value="">All</option>
//                 {uniqueValues.LOCA_ID_OFFL_ONL.map(value => (
//                   <option key={value} value={value}>{value}</option>
//                 ))}
//               </select>
//             </div>

//             {/* BR_AP_TERMINAL Dropdown */}
//             <div>
//               <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>BR/AP Terminal</label>
//               <select
//                 value={filters.BR_AP_TERMINAL}
//                 onChange={(e) => handleFilterChange(e, "BR_AP_TERMINAL")}
//                 style={{ width: "100%", padding: "8px" }}
//               >
//                 <option value="">All</option>
//                 {uniqueValues.BR_AP_TERMINAL.map(value => (
//                   <option key={value} value={value}>{value}</option>
//                 ))}
//               </select>
//             </div>

//             {/* CITY Dropdown */}
//             <div>
//               <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>City</label>
//               <select
//                 value={filters.CITY}
//                 onChange={(e) => handleFilterChange(e, "CITY")}
//                 style={{ width: "100%", padding: "8px" }}
//               >
//                 <option value="">All</option>
//                 {uniqueValues.CITY.map(value => (
//                   <option key={value} value={value}>{value}</option>
//                 ))}
//               </select>
//             </div>

//             {/* SEGMENT Dropdown */}
//             <div>
//               <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Segment</label>
//               <select
//                 value={filters.SEGMENT}
//                 onChange={(e) => handleFilterChange(e, "SEGMENT")}
//                 style={{ width: "100%", padding: "8px" }}
//               >
//                 <option value="">All</option>
//                 {uniqueValues.SEGMENT.map(value => (
//                   <option key={value} value={value}>{value}</option>
//                 ))}
//               </select>
//             </div>

//             {/* Days Remaining Filter */}
//             <div>
//               <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
//                 Days Remaining ≤
//               </label>
//               <input
//                 type="number"
//                 value={filters.DAYS_REMAINING}
//                 onChange={(e) => handleFilterChange(e, "DAYS_REMAINING")}
//                 style={{ width: "100%", padding: "8px" }}
//                 placeholder="Enter days"
//                 min="0"
//               />
//             </div>

//             <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end" }}>
//               <button 
//                 onClick={() => setFilters({
//                   TERMINAL_TYPE: "",
//                   LOCA_ID_OFFL_ONL: "",
//                   BR_AP_TERMINAL: "",
//                   CITY: "",
//                   SEGMENT: "",
//                   DAYS_REMAINING: ""
//                 })}
//                 style={{ 
//                   padding: "8px 16px",
//                   backgroundColor: "#e9d0b8",
//                   border: "1px solid #ccc",
//                   borderRadius: "4px",
//                   cursor: "pointer",
//                   color:'black'
//                 }}
//               >
//                 Clear All Filters
//               </button>
//             </div>
//           </div>
//         )}
//       </div>

//       {/* Add Button */}
//       <button 
//         onClick={() => setShowAddForm(!showAddForm)} 
//         style={{ 
//           marginBottom: 20,
//           padding: "8px 16px",
//           backgroundColor: "#4CAF50",
//           color: "white",
//           border: "none",
//           borderRadius: "4px",
//           cursor: "pointer"
//         }}
//       >
//         {showAddForm ? "Cancel" : "Add New Record"}
//       </button>

//       {/* Add Form - shows when Add button clicked */}
//       {showAddForm && (
//         <div
//           style={{
//             marginBottom: 20,
//             padding: 15,
//             border: "1px solid #ccc",
//             borderRadius: 5,
//             backgroundColor: "#fafafa",
//             maxHeight: "60vh",
//             overflowY: "auto",
//           }}
//         >
//           <h3>Add New Record</h3>
//           <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
//             {allColumns.map((col) => (
//               <div key={col} style={{ minWidth: 200, display: "flex", flexDirection: "column" }}>
//                 <label style={{ fontWeight: "bold" }}>{col}</label>
//                 <input
//                   type="text"
//                   value={newRecord[col] || ""}
//                   onChange={(e) => handleNewChange(e, col)}
//                 />
//               </div>
//             ))}
//           </div>
//           <button 
//             onClick={handleAddRecord} 
//             style={{ 
//               marginTop: 15,
//               padding: "8px 16px",
//               backgroundColor: "#4CAF50",
//               color: "white",
//               border: "none",
//               borderRadius: "4px",
//               cursor: "pointer"
//             }}
//           >
//             Submit
//           </button>
//         </div>
//       )}

//       {/* Main Table with visibleColumns */}
//       <table border="1" cellPadding="5" cellSpacing="0" style={{ width: "100%" }}>
//         <thead>
//           <tr>
//             {visibleColumns.map((col) => (
//               <th key={col}>{col}</th>
//             ))}
//             <th>Actions</th>
//           </tr>
//         </thead>
//         <tbody>
//           {filteredRecords.map((record) => (
//             <tr key={record.sno}>
//               {visibleColumns.map((col) => (
//                 <td key={col}>
//                 {["FO_NISM", "COMMODITY_NISM", "CDS_NISM"].includes(col) ? (
//                   <>
//                     <div>{new Date(record[col]).toISOString().split('T')[0]}</div>
//                     <div style={{
//                       fontSize: '1.1em',
//                       fontWeight: 'bold',
//                       color: getStatusColor(calculateDaysRemaining(record[col]).status)
//                     }}>
//                       {calculateDaysRemaining(record[col]).text}
//                     </div>
//                   </>
//                 ) : (
//                   record[col]
//                 )}
//               </td>
              
//               ))}
//               <td>
//                 <button 
//                   onClick={() => handleView(record)}
//                   style={{
//                     padding: "5px 10px",
//                     backgroundColor: "#2196F3",
//                     color: "white",
//                     border: "none",
//                     borderRadius: "3px",
//                     cursor: "pointer"
//                   }}
//                 >
//                   View
//                 </button>
//               </td>
//             </tr>
//           ))}
//         </tbody>
//       </table>

//       {/* View/Edit Panel */}
//       {viewRecord && (
//         <div
//           style={{
//             position: "fixed",
//             top: 0,
//             right: 0,
//             width: "50vw",
//             height: "100vh",
//             backgroundColor: "#f0f0f0",
//             boxShadow: "-3px 0 5px rgba(0,0,0,0.2)",
//             padding: 20,
//             overflowY: "auto",
//             zIndex: 1000,
//           }}
//         >
//           <h3>View Record - SNO: {viewRecord.sno}</h3>
//           <button 
//             onClick={handleCloseView}
//             style={{
//               padding: "5px 10px",
//               backgroundColor: "#f44336",
//               color: "white",
//               border: "none",
//               borderRadius: "3px",
//               cursor: "pointer"
//             }}
//           >
//             Close
//           </button>

//           <table>
//             <tbody>
//               {allColumns.map((col) => (
//                 <tr key={col}>
//                   <td style={{ fontWeight: "bold", paddingRight: 10 }}>{col}</td>
//                   <td>
//                     {editRecord && editRecord.sno === viewRecord.sno ? (
//                       <input
//                         value={editRecord[col] || ""}
//                         onChange={(e) => handleChange(e, col)}
//                         style={{ minWidth: 200 }}
//                       />
//                     ) : (
//                       ["FO_NISM", "COMMODITY_NISM", "CDS_NISM"].includes(col) ? (
//                         <>
//                           <div>{viewRecord[col]}</div>
//                           <div style={{
//                             fontSize: '0.8em',
//                             color: getStatusColor(calculateDaysRemaining(viewRecord[col]).status)
//                           }}>
//                             {calculateDaysRemaining(viewRecord[col]).text}
//                           </div>
//                         </>
//                       ) : (
//                         viewRecord[col]
//                       )
//                     )}
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>

//           {editRecord && editRecord.sno === viewRecord.sno ? (
//             <>
//               <button 
//                 onClick={handleSave} 
//                 style={{ 
//                   marginTop: 20,
//                   padding: "8px 16px",
//                   backgroundColor: "#4CAF50",
//                   color: "white",
//                   border: "none",
//                   borderRadius: "4px",
//                   cursor: "pointer"
//                 }}
//               >
//                 Save
//               </button>
//               <button 
//                 onClick={() => setEditRecord(null)} 
//                 style={{ 
//                   marginLeft: 10,
//                   padding: "8px 16px",
//                   backgroundColor: "#f44336",
//                   color: "white",
//                   border: "none",
//                   borderRadius: "4px",
//                   cursor: "pointer"
//                 }}
//               >
//                 Cancel
//               </button>
//             </>
//           ) : (
//             <button 
//               onClick={() => handleEdit(viewRecord)} 
//               style={{ 
//                 marginTop: 20,
//                 padding: "8px 16px",
//                 backgroundColor: "#2196F3",
//                 color: "white",
//                 border: "none",
//                 borderRadius: "4px",
//                 cursor: "pointer"
//               }}
//             >
//               Edit
//             </button>
//           )}
//         </div>
//       )}
//     </div>
//   );
// }

